import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { useUpdateStorePolicy } from "@/api/mutations";
import { useStaffList } from "@/api/queries";
import { type HandoffAssignment, OFFER_READINESS_FIELDS, type OfferReadinessField, type StoreDetail, type StoreOfferPolicy } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { formErrorsFrom } from "@/lib/form-errors";
import { ASSIGNMENT_LABEL, offerPolicyErrors, READINESS_LABEL } from "@/lib/offer-policy";

/** Form values: money in whole dollars, everything as typed. */
interface Values {
  maxPercentOfValuation: string;
  increment: string;
  autoApproveMax: string;
  valuationMaxAgeDays: string;
  openAtCeilingGapPercent: string;
  askPriceGapPercent: string;
  openingBelowCeiling: string;
  maxStep: string;
  handoffGap: string;
  requiredInfo: OfferReadinessField[];
  assignment: HandoffAssignment;
  /** "" = no Rogue admin alerted. */
  notifyAdminId: string;
}

const dollars = (cents: number) => String(cents / 100);
const fromPolicy = (p: StoreOfferPolicy): Values => ({
  maxPercentOfValuation: String(p.offers.maxPercentOfValuation),
  increment: dollars(p.offers.incrementCents),
  autoApproveMax: dollars(p.offers.autoApproveMaxCents),
  valuationMaxAgeDays: String(p.offers.valuationMaxAgeDays),
  openAtCeilingGapPercent: String(p.negotiation.openAtCeilingGapPercent),
  askPriceGapPercent: String(p.negotiation.askPriceGapPercent),
  openingBelowCeiling: dollars(p.negotiation.openingBelowCeilingCents),
  maxStep: dollars(p.negotiation.maxStepCents),
  handoffGap: dollars(p.negotiation.handoffGapCents),
  requiredInfo: p.negotiation.requiredInfo,
  assignment: p.handoff.assignment,
  notifyAdminId: p.handoff.notifyAdminId ?? "",
});
const num = (v: string) => (v.trim() === "" ? Number.NaN : Number(v));
const cents = (v: string) => Math.round(num(v) * 100);
const toPolicy = (v: Values): StoreOfferPolicy => ({
  offers: {
    maxPercentOfValuation: num(v.maxPercentOfValuation),
    incrementCents: cents(v.increment),
    autoApproveMaxCents: cents(v.autoApproveMax),
    valuationMaxAgeDays: num(v.valuationMaxAgeDays),
  },
  negotiation: {
    requiredInfo: v.requiredInfo,
    openAtCeilingGapPercent: num(v.openAtCeilingGapPercent),
    askPriceGapPercent: num(v.askPriceGapPercent),
    openingBelowCeilingCents: cents(v.openingBelowCeiling),
    maxStepCents: cents(v.maxStep),
    handoffGapCents: cents(v.handoffGap),
  },
  handoff: { assignment: v.assignment, notifyAdminId: v.notifyAdminId || null },
});

/** Form field → the backend's error key (e.g. `negotiation.askPriceGapPercent`). */
const ERROR_KEY: Record<Exclude<keyof Values, "requiredInfo" | "assignment" | "notifyAdminId">, string> = {
  maxPercentOfValuation: "offers.maxPercentOfValuation",
  increment: "offers.incrementCents",
  autoApproveMax: "offers.autoApproveMaxCents",
  valuationMaxAgeDays: "offers.valuationMaxAgeDays",
  openAtCeilingGapPercent: "negotiation.openAtCeilingGapPercent",
  askPriceGapPercent: "negotiation.askPriceGapPercent",
  openingBelowCeiling: "negotiation.openingBelowCeilingCents",
  maxStep: "negotiation.maxStepCents",
  handoffGap: "negotiation.handoffGapCents",
};
type NumberField = keyof typeof ERROR_KEY;

/**
 * The store's offer policy: how Rogue opens, negotiates and hands off. Edited by Rogue staff
 * only (dealers see it read-only). The backend validates and audits every change.
 */
export function StorePolicyDialog({ open, onOpenChange, store }: { open: boolean; onOpenChange: (o: boolean) => void; store: StoreDetail }) {
  const [values, setValues] = useState<Values>(() => fromPolicy(store.policy));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const update = useUpdateStorePolicy(store.id);
  useEffect(() => {
    if (open) {
      setValues(fromPolicy(store.policy));
      setErrors({});
      setFormError(null);
    }
  }, [open, store.policy]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const policy = toPolicy(values);
    const found = offerPolicyErrors(policy);
    if (Object.keys(found).length) return setErrors(found);
    setErrors({});
    setFormError(null);
    update.mutate(policy, {
      onSuccess: () => {
        toast.success("Offer policy updated");
        onOpenChange(false);
      },
      onError: (err) => {
        const { fields, form } = formErrorsFrom(err);
        setErrors(fields);
        setFormError(form);
      },
    });
  };

  const numberField = (name: NumberField, label: string, opts: { unit: "$" | "%" | "days"; hint?: string }) => {
    const error = errors[ERROR_KEY[name]];
    return (
      <Field data-invalid={Boolean(error)} className="gap-1">
        <FieldLabel htmlFor={`policy-${name}`}>{label}</FieldLabel>
        <InputGroup>
          {opts.unit === "$" ? (
            <InputGroupAddon>
              <InputGroupText>$</InputGroupText>
            </InputGroupAddon>
          ) : null}
          <InputGroupInput
            id={`policy-${name}`}
            inputMode="decimal"
            value={values[name]}
            onChange={(e) => setValues((v) => ({ ...v, [name]: e.target.value }))}
            aria-invalid={Boolean(error)}
            autoComplete="off"
          />
          {opts.unit !== "$" ? (
            <InputGroupAddon align="inline-end">
              <InputGroupText>{opts.unit}</InputGroupText>
            </InputGroupAddon>
          ) : null}
        </InputGroup>
        {opts.hint && !error ? <FieldDescription className="text-xs">{opts.hint}</FieldDescription> : null}
        <FieldError className="text-xs">{error}</FieldError>
      </Field>
    );
  };

  const toggleInfo = (field: OfferReadinessField, on: boolean) =>
    setValues((v) => ({
      ...v,
      requiredInfo: on ? OFFER_READINESS_FIELDS.filter((f) => f === field || v.requiredInfo.includes(f)) : v.requiredInfo.filter((f) => f !== field),
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>Offer policy</DialogTitle>
            <DialogDescription>
              How Rogue opens, negotiates and hands off for {store.name}. Rogue never offers more than the max offer or the seller's price. Dealers see this
              read-only.
            </DialogDescription>
          </DialogHeader>
          <div className="my-4 max-h-[65vh] space-y-5 overflow-y-auto pr-1">
            {formError ? (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            ) : null}
            <fieldset className="space-y-3">
              <legend className="mb-2 font-semibold text-xs uppercase tracking-wide">Opening offer</legend>
              <p className="text-muted-foreground text-xs">
                The gap is how far the seller's price is above the max offer, as a % of their price. Rogue opens with exactly one figure: the max, or a set
                amount below it.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {numberField("openAtCeilingGapPercent", "Open at the max above a gap of", {
                  unit: "%",
                  hint: "A bigger gap: Rogue opens at the max offer.",
                })}
                {numberField("openingBelowCeiling", "Otherwise open this far below the max", { unit: "$", hint: "Never above the seller's price." })}
                {numberField("askPriceGapPercent", "Ask their price first above a gap of", {
                  unit: "%",
                  hint: "Or when no price is known. Asked once, never again.",
                })}
              </div>
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-2 font-semibold text-xs uppercase tracking-wide">Negotiation</legend>
              <div className="grid gap-3 sm:grid-cols-3">
                {numberField("maxStep", "Raise at most", { unit: "$", hint: "Per offer, never past the max." })}
                {numberField("handoffGap", "Hand off on a gap of", { unit: "$", hint: "At the max, seller this close above it." })}
                <Field className="gap-1">
                  <FieldLabel htmlFor="policy-assignment">Unassigned leads at handoff go to</FieldLabel>
                  <NativeSelect
                    id="policy-assignment"
                    className="w-full"
                    value={values.assignment}
                    onChange={(e) => setValues((v) => ({ ...v, assignment: e.target.value as HandoffAssignment }))}
                  >
                    {(Object.keys(ASSIGNMENT_LABEL) as HandoffAssignment[]).map((a) => (
                      <NativeSelectOption key={a} value={a}>
                        {ASSIGNMENT_LABEL[a]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
              </div>
              <HandoffAlertField
                value={values.notifyAdminId}
                onChange={(notifyAdminId) => setValues((v) => ({ ...v, notifyAdminId }))}
                error={errors["handoff.notifyAdminId"]}
              />
              <Field className="gap-1">
                <FieldLabel>Needed before an offer</FieldLabel>
                <FieldDescription className="text-xs">
                  Year, make and model are always needed. Rogue asks the seller for the items checked here, and only those.
                </FieldDescription>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {OFFER_READINESS_FIELDS.map((f) => (
                    <label key={f} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={values.requiredInfo.includes(f)}
                        onCheckedChange={(on) => toggleInfo(f, Boolean(on))}
                        aria-label={READINESS_LABEL[f]}
                      />
                      {READINESS_LABEL[f]}
                    </label>
                  ))}
                </div>
              </Field>
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-2 font-semibold text-xs uppercase tracking-wide">Offers</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {numberField("maxPercentOfValuation", "Max offer from an API valuation", {
                  unit: "%",
                  hint: "Of the valuation, for Offer API connectors that return a valuation.",
                })}
                {numberField("increment", "Offer increment", { unit: "$", hint: "Offers are multiples of this." })}
                {numberField("valuationMaxAgeDays", "Valuation valid for", { unit: "days" })}
                {numberField("autoApproveMax", "Staff offers approved automatically up to", { unit: "$", hint: "$0: staff offers always need approval." })}
              </div>
            </fieldset>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Spinner data-icon="inline-start" /> : null}
              Save policy
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ACTIVE_STAFF = { page: 1, pageSize: 100, sort: "name", order: "asc", filters: { status: "ACTIVE" } } as const;

/** One of our admins also alerted on Slack (tagged by their Slack member id) on every handoff. */
function HandoffAlertField({ value, onChange, error }: { value: string; onChange: (id: string) => void; error?: string }) {
  const staff = useStaffList(ACTIVE_STAFF);
  const admins = staff.data?.items ?? [];
  const current = admins.find((a) => a.id === value);
  return (
    <Field data-invalid={Boolean(error)} className="gap-1">
      <FieldLabel htmlFor="policy-notify-admin">Also alert a Rogue admin (Slack)</FieldLabel>
      <NativeSelect id="policy-notify-admin" className="w-full sm:w-80" value={value} onChange={(e) => onChange(e.target.value)} disabled={staff.isPending}>
        <NativeSelectOption value="">Nobody</NativeSelectOption>
        {value && !current ? <NativeSelectOption value={value}>{staff.isPending ? "Loading…" : "An admin who's no longer active"}</NativeSelectOption> : null}
        {admins.map((a) => (
          <NativeSelectOption key={a.id} value={a.id}>
            {a.name}
            {a.slackUserId ? "" : " (no Slack id)"}
          </NativeSelectOption>
        ))}
      </NativeSelect>
      <FieldDescription className="text-xs">
        On every handoff or escalation the admin is tagged in Slack with a link to the lead.
        {current && !current.slackUserId ? " They have no Slack member id yet, so they're named, not tagged." : ""}
      </FieldDescription>
      <FieldError>{error}</FieldError>
    </Field>
  );
}
